// import { authStore } from "../_auth";
import { merge } from "lodash";
import {RequestProps} from "./useFetch";

export interface SysInfoProps {
    timeOpened: Date,
    timezone: string,
    pageon: string,
    referrer: string,
    previousSites: number,
    browserName: string,
    browserEngine: string,
    browserVersion1a: string,
    browserVersion1b: string,
    browserLanguage: string,
    browserOnline: boolean,
    browserPlatform: string,
    javaEnabled: boolean,
    dataCookiesEnabled: boolean,
    dataCookies1: string,
    // dataCookies2: string,
    dataStorage: {[key: string]: string},
    sizeScreenW: number,
    sizeScreenH: number,
    // sizeDocW: number,
    // sizeDocH: number,
    sizeInW: number,
    sizeInH: number,
    sizeAvailW: number,
    sizeAvailH: number,
    scrColorDepth: number,
    scrPixelDepth: number,
    latitude: number | null,
    longitude: number | null
};

export const DateFormatEnum = {
    Date: "date",
    Time: "time",
    Datetime: "datetime",
};

export type DateFormatType = typeof DateFormatEnum[keyof typeof DateFormatEnum];

/**
 * ```js
 * parseQueryResult(queryString);
 * ```
 * Parse query string result from URL.
 * This method only accepts the query string portion of the URL.
 *
 * Returns an object as dictionary of {Key, Value} pairs if present.
 *
 * @param {string} queryString
 * @return {object} dictionary of {key,value} pairs
 */

const parseQueryResult = (queryString: string): {[key: string]: string} => {
    if (queryString.indexOf('#') > -1) {
      queryString = queryString.substring(0, queryString.indexOf('#'));
    }

    const queryParams = queryString.split('&');

    const parsedQuery: {[key: string]: string} = {};
    queryParams.forEach(qp => {
      const [key, val] = qp.split('=');
      parsedQuery[key] = decodeURIComponent(val);
    });

    return parsedQuery;
}


/**
 * ```js
 * createQueryParams(object);
 * ```
 * Parse dictionary of {key,value} pairs.
 *
 * Returns a string of {Key, Value} pairs in Form URL encoded format.
 *
 * @param {object} object dictionary of {key,value} pairs
 * @return {string} string as form URL encoded string of key, value pairs
 */
const createQueryParams = (params: {[key: string]: any}): string => {
  return Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}


/**
 * ```js
 * sysInfo();
 * ```
 * Get System/Browser Information.
 *
 * Returns an object containing many system/browser details.
 *
 * @return {object} dictionary of {key,value} pairs
 */
const sysInfo = (): SysInfoProps => {
    let latitude: number | null = null;
    let longitude: number | null = null;
    const location: Geolocation = window.navigator && window.navigator.geolocation;

    if (location) {
      location.getCurrentPosition(position => {
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      });
    }

    const tzOffsetFmt = (): string => {
        let ind = "";
        const offset = new Date().getTimezoneOffset() / 60;

        if(offset > 0) {
            ind = "-";
        }
        else if (offset < 0) {
            ind = "+";
        }
        return `${ind} ${offset}`;
    }

    return {
        timeOpened: new Date(),
        timezone: tzOffsetFmt(),
        pageon: window.location.pathname,
        referrer: document.referrer,
        previousSites: window.history.length,
        browserName: window.navigator.appName,
        browserEngine: window.navigator.product,
        browserVersion1a: window.navigator.appVersion,
        browserVersion1b: navigator.userAgent,
        browserLanguage: navigator.language,
        browserOnline: navigator.onLine,
        browserPlatform: navigator.platform,
        javaEnabled: navigator.javaEnabled(),
        dataCookiesEnabled: navigator.cookieEnabled,
        dataCookies1: document.cookie,
        // dataCookies2: decodeURIComponent(document.cookie.split(';')),
        dataStorage: localStorage,
        sizeScreenW: window.screen.width,
        sizeScreenH: window.screen.height,
        // sizeDocW: window.document.width,
        // sizeDocH: window.document.height,
        sizeInW: window.innerWidth,
        sizeInH: window.innerHeight,
        sizeAvailW: window.screen.availWidth,
        sizeAvailH: window.screen.availHeight,
        scrColorDepth: window.screen.colorDepth,
        scrPixelDepth: window.screen.pixelDepth,
        latitude: latitude,
        longitude: longitude
    };
}

/* Utils Date Functions */
/**
 * ```js
 * now();
 * ```
 * This method gets the current time formatted hh:mm:ss am.
 *
 * Returns the current time formatted hh:mm:ss am.
 *
 * @return {string} string with current time hh:mm:ss am
 */
const now = (): string => {
    const date = new Date();
    if (!date) {
        return "";
    }

    let hour = date.getHours(),
        minute = date.getMinutes(),
        second = date.getSeconds(),
        hourFormatted = hour % 12 || 12, // hour returned in 24 hour format
        minuteFormatted = minute < 10 ? "0" + minute : minute,
        morning = hour < 12 ? "AM" : "PM";

    return hourFormatted + ":" + minuteFormatted + ":" + second + morning;
}

/**
 * ```js
 * today();
 * ```
 * This method gets the current date formatted mm/dd/yyyy.
 *
 * Returns the current time formatted hh:mm:ss am.
 *
 * @return {string} string with current date formatted as mm/dd/yyyy
 */
const today = (): string => {
    const date = new Date();
    if (!date) {
        return "";
    }

    let year = date.getFullYear(),
        month = date.getMonth() + 1, // months are zero indexed
        day = date.getDate();

    return month + "/" + day + "/" + year;
}

/**
 * ```js
 * format(date: Date);
 * ```
 * This method returns a formatted string for date/time
 *     supports the following formats:
 *     Date: (mm/dd/yyyy)
 *     Time: (hh:mm:ss am)
 *     Datetime: (mm/dd/yyyy hh:mm:ss am)
 *
 * Returns a formatted string for the passed in date/time object.
 *
 * @param {Date} date Date
 * @param {DateFormatEnum} format DateFormatEnum
 * @return {string} string with passed in date formatted
 */
const format = (date: Date, format: DateFormatType = DateFormatEnum.Datetime): string => {
    if (!date) {
        return "";
    }

    let year = date.getFullYear(),
        month = date.getMonth() + 1, // months are zero indexed
        day = date.getDate(),
        hour = date.getHours(),
        minute = date.getMinutes(),
        second = date.getSeconds(),
        hourFormatted = hour % 12 || 12, // hour returned in 24 hour format
        minuteFormatted = minute < 10 ? "0" + minute : minute,
        morning = hour < 12 ? "AM" : "PM",
        retVal = "";

    switch (format) {
        case DateFormatEnum.Date:
            retVal = month + "/" + day + "/" + year;
            break;
        case DateFormatEnum.Time:
            retVal = hourFormatted + ":" + minuteFormatted + ":" + second + morning;
            break;
        case DateFormatEnum.Datetime:
            retVal = month + "/" + day + "/" + year + " " +
                hourFormatted + ":" + minuteFormatted + ":" + second + morning;
            break;
        default:
            retVal = "Invalid DateFormat argument";
            break;
    }

    return retVal;
}

/**
 * Sleep Utils
**/

const sleep = (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Fetch Utils
**/

// RegExp to handle ISO and MS Ajax string date formats
//^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d+)?)(?:(\+|-)([\d|:]*)|Z)?$ -> "2014-01-01T23:28:56.782Z" or "2014-01-01T23:28:56.782-6:00"
//^\/Date\((d|-|.*)\)[\/|\\]$ -> /Date(1525730400000)/
    // 2009-04-03T22:01:46Z
const dateConverter = (key: string, value: any): any => {
    const parseMsAjaxDate = false;
    const reISO = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d+)?)(?:(\+|-)([\d|:]*)|Z)?$/;
    const reMsAjax = /^\/Date\((d|-|.*)\)[/|\\]$/;

    let a: RegExpExecArray | null = null;
    if (typeof value === 'string') {
        a = reISO.exec(value);
        if (a) {
            return new Date(value);
        }

        if (parseMsAjaxDate) {
            a = reMsAjax.exec(value);
            if (a) {
                let b = a[1].split(/[-+,.]/);
                return new Date(b[0] ? +b[0] : 0 - +b[1]);
            }
        }
    }
    return value;
};

/**
 * ```js
 * doFetch<T>(url: RequestInfo | string, request: RequestInit);
 * ```
 * Returns a Response object when the fetch() promises are resolved.
 * and formats date strings as date/time (mm/dd/yyyy hh:mm:ss am).
 *
 *
 * @param {RequestInfo | string} url object
 * @param {RequestInfo | string} request object
 * @return {Promise} string with current date formatted as mm/dd/yyyy hh:mm:ss am
 */
const doFetch = async <T>(url: RequestInfo | string, request: RequestInit): Promise<T> => {
    try {
        const response = await fetch(url, request);
        if (response.ok) {
            // return await response.json();
            // use following technique to parse date from json response
            // response.json() does not support date parsing
            return await response.text().then((text) => text ? JSON.parse(text, dateConverter) : {});
        } else {
            const err_txt = await response.text();
            const err_json = JSON.parse(err_txt, dateConverter);

            const msg = err_json ? `${err_json.error} (${err_json.code}) - ${err_json.message}` : err_txt;
            const error = `Error fetching data. StatusText: ${msg}, from URL: ${url}.`;
            return err_json;
        }
    } catch (error) {
        //Network error
        throw error;
    }
}

const initFetchRequest = (
    request: RequestInit,
    auth_token: string | undefined = undefined): RequestInit => {

    let defaultHeaders: HeadersInit = {
        'content-type': 'application/json',
        'accept': 'application/json',
    };
    if (auth_token !== undefined) {
        defaultHeaders['authorization'] = `Bearer ${auth_token}`;
    }

    let newRequest: RequestInit = {
        mode: "cors",
        headers: defaultHeaders,
        // Credentials is needed to send/receive cookies across domains,
        // uncomment if using CookieAuthentication with Django Api
        // credentials: "include"
    };

    //Get some defaults, use lodash to combine the objects
    merge(newRequest, request);

    return newRequest;
}

/**
 * ```js
 * daFetch<T>(url: RequestInfo | string, request: RequestInit);
 * ```
 * This method returns a formatted string for date/time (mm/dd/yyyy hh:mm:ss am).
 *
 * Returns a Response object when the fetch() promises are resolved.
 *
 * @param {RequestInfo | string} url object
 * @param {RequestInfo | string} request object
 * @param {string | undefined} auth_token object
 * @return {Promise} string with current date formatted as mm/dd/yyyy hh:mm:ss am
 */
const daFetch = async <T>(
    url: RequestInfo | string,
    request: RequestInit,
    auth_token: string | undefined = undefined): Promise<T> => {

    let newRequest = initFetchRequest(request, auth_token);

    return await doFetch(url, newRequest);
}

/**
 * ```js
 * daFetch<T>(url: RequestInfo | string, request: RequestInit);
 * ```
 * This method returns a formatted string for date/time (mm/dd/yyyy hh:mm:ss am).
 *
 * Returns a Response object when the fetch() promises are resolved.
 *
 * @param {RequestInfo | string} url object
 * @param {RequestInfo | string} request object
 * @param {string | undefined} auth_token object
 * @return {Promise} string with current date formatted as mm/dd/yyyy hh:mm:ss am
 */
const daFetchUpload = async <T>(
    url: RequestInfo | string,
    request: RequestInit,
    auth_token: string | undefined = undefined): Promise<T> => {

    // Initialize request with default headers
    let newRequest = initFetchRequest(request, auth_token);

    // Remove Content-Type header from File upload requests
    // Browser will automatically add appropriate content-type
    // headers for multi-part Form requests, including boundary
    let headers: Headers = new Headers(newRequest.headers);
    headers.delete("content-type");

    newRequest.headers = headers as HeadersInit;

    return await doFetch(url, newRequest);
}

const getCrypto = () => {
  //ie 11.x uses msCrypto
  return (window.crypto || (window as any).msCrypto) as Crypto;
};

const createRandomString = () => {
  const charset =
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_~.';
  let random = '';
  const randomValues = Array.from(
    getCrypto().getRandomValues(new Uint8Array(43))
  );
  randomValues.forEach(v => (random += charset[v % charset.length]));
  return random;
};

const encodeB64 = (value: string) => btoa(value);
const decodeB64 = (value: string) => atob(value);

export const daUtils = {
    /* URL Encoding functions */
    parseQueryResult,
    createQueryParams,

    /* Base 64 Encoding/Decoding functions */
    createRandomString,
    getCrypto,
    encodeB64,
    decodeB64,

    /* Date/Time Functions */
    now,
    today,
    format,

    /* Ajax Functions */
    daFetch,
    daFetchUpload,

    /* Other */
    sleep,
    sysInfo,
};

